import { autocompletion, closeBrackets, closeBracketsKeymap, completeAnyWord } from '@codemirror/autocomplete'
import { json, jsonParseLinter } from '@codemirror/lang-json'
import { bracketMatching, foldGutter, foldKeymap, indentOnInput, indentUnit } from '@codemirror/language'
import { type Diagnostic, linter, lintGutter, setDiagnostics } from '@codemirror/lint'
import { searchKeymap } from '@codemirror/search'
import { Annotation, Compartment, EditorState, Transaction } from '@codemirror/state'
import {
  EditorView,
  placeholder as editorPlaceholder,
  highlightActiveLine,
  highlightActiveLineGutter,
  keymap,
  lineNumbers,
} from '@codemirror/view'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import { styled } from '@mui/material/styles'
import { minimalSetup } from 'codemirror'
import React from 'react'

import PzDetailsDialog from '@/app/component/atom/pz-details-dialog'
import { formatPzPipeline, type PzExpression } from '@/app/component/atom/pz-expression'
import { getPzCatalog } from '@/app/component/atom/pz-metadata'
import type { PersonalizationType } from '@/app/types'

import FormControl from './form-control'
import { useCodePersonalization } from './input-code-personalization'

export type InputCodeRef = {
  focus: () => void
  insertText: (text: string) => void
}

type Props = {
  actions?: React.ReactNode
  disabledText?: string
  editable?: boolean
  error?: string
  height?: string
  highlightLine?: boolean
  info?: React.ReactNode
  inputLanguage?: 'json' | 'text'
  label?: string
  personalizationList?: PersonalizationType
  placeholder?: string
  readOnly?: boolean
  showFoldGutter?: boolean
  value: string
  width?: string
  onBlur?: (value: string) => void
  onChange: (value: string) => void
  onFocus?: () => void
  onValidate?: (diagnostics: readonly Diagnostic[]) => void
}

const editorTheme = EditorView.theme({
  '&': { fontSize: '12px' },
  '&.cm-focused': { outline: 'none' },
  '.cm-scroller': { overflow: 'auto' },
})

const externalValue = Annotation.define<boolean>()
const validateJSON = jsonParseLinter()
// Empty bodies are permitted here; required-field validation belongs to the caller.
const diagnostics = (view: EditorView) => (view.state.doc.length ? validateJSON(view) : [])

const InputCode = React.forwardRef<InputCodeRef, Props>(
  (
    {
      actions,
      disabledText,
      editable = true,
      error,
      height = '80px',
      highlightLine = true,
      info,
      inputLanguage = 'text',
      label = 'Code',
      personalizationList,
      placeholder,
      readOnly = false,
      showFoldGutter = true,
      value,
      width = '100%',
      onBlur,
      onChange,
      onFocus,
      onValidate,
    },
    ref,
  ) => {
    const containerRef = React.useRef<HTMLDivElement>(null)
    const viewRef = React.useRef<EditorView | null>(null)
    const focusTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null)
    const config = React.useMemo(() => new Compartment(), [])
    const personalizationConfig = React.useMemo(() => new Compartment(), [])
    const [advanced, setAdvanced] = React.useState(false)
    const [selectedExpression, setSelectedExpression] = React.useState<PzExpression>()
    const trigger = React.useRef<HTMLElement | null>(null)
    const catalog = React.useMemo(() => getPzCatalog(personalizationList || {}), [personalizationList])
    const byReference = React.useMemo(() => new Map(catalog.map((item) => [item.value, item])), [catalog])
    const expressionDetails = React.useMemo(
      () =>
        selectedExpression && {
          text: selectedExpression.text,
          pipelineStages: formatPzPipeline(selectedExpression.pipelineStages, byReference),
        },
      [byReference, selectedExpression],
    )
    const { extension: personalization, portals } = useCodePersonalization(
      catalog,
      !advanced,
      (expression, element) => {
        trigger.current = element
        setSelectedExpression(expression)
      },
    )
    const sizeTheme = React.useMemo(() => EditorView.theme({ '&': { height, width } }), [height, width])
    const current = React.useRef({ inputLanguage, value, onBlur, onChange, onFocus, onValidate })
    current.current = { inputLanguage, value, onBlur, onChange, onFocus, onValidate }

    React.useImperativeHandle(
      ref,
      () => ({
        focus: () => viewRef.current?.focus(),
        insertText: (text) => {
          const view = viewRef.current
          if (!view || view.state.readOnly) return
          const position = view.state.selection.main.head
          view.dispatch({
            changes: { from: position, insert: text },
            selection: { anchor: position + text.length },
            scrollIntoView: true,
            userEvent: 'input',
          })
          // Restore focus after the personalization menu closes and restores its own focus.
          if (focusTimeout.current) clearTimeout(focusTimeout.current)
          focusTimeout.current = setTimeout(() => viewRef.current?.focus(), 0)
        },
      }),
      [],
    )

    React.useEffect(() => {
      if (!containerRef.current) return
      const view = new EditorView({
        parent: containerRef.current,
        state: EditorState.create({
          doc: current.current.value,
          extensions: [
            minimalSetup,
            editorTheme,
            lineNumbers(),
            indentOnInput(),
            bracketMatching(),
            closeBrackets(),
            autocompletion({ activateOnTyping: false, override: [completeAnyWord] }),
            keymap.of([...closeBracketsKeymap, ...foldKeymap, ...searchKeymap]),
            indentUnit.of('  '),
            EditorState.tabSize.of(2),
            config.of([]),
            personalizationConfig.of([]),
            EditorView.updateListener.of((update) => {
              if (!update.docChanged) return
              const callbacks = current.current
              if (!update.transactions.some((transaction) => transaction.annotation(externalValue))) {
                callbacks.onChange(update.state.doc.toString())
              }
            }),
          ],
        }),
      })
      const handleFocusIn = (event: FocusEvent) => {
        if (event.relatedTarget instanceof Node && view.dom.contains(event.relatedTarget)) return
        current.current.onFocus?.()
      }
      const handleFocusOut = (event: FocusEvent) => {
        if (event.relatedTarget instanceof Node && view.dom.contains(event.relatedTarget)) return
        const callbacks = current.current
        const errors = callbacks.inputLanguage === 'json' ? diagnostics(view) : []
        view.dispatch(setDiagnostics(view.state, errors))
        callbacks.onValidate?.(errors)
        callbacks.onBlur?.(view.state.doc.toString())
      }
      view.dom.addEventListener('focusin', handleFocusIn)
      view.dom.addEventListener('focusout', handleFocusOut)
      viewRef.current = view
      return () => {
        if (focusTimeout.current) clearTimeout(focusTimeout.current)
        view.dom.removeEventListener('focusin', handleFocusIn)
        view.dom.removeEventListener('focusout', handleFocusOut)
        view.destroy()
        viewRef.current = null
      }
    }, [config, personalizationConfig])

    React.useEffect(() => {
      viewRef.current?.dispatch({ effects: personalizationConfig.reconfigure(personalization) })
    }, [personalization, personalizationConfig])

    React.useEffect(() => {
      const view = viewRef.current
      if (!view) return
      view.dispatch({
        effects: config.reconfigure([
          ...(inputLanguage === 'json' ? [json(), lintGutter(), linter(diagnostics, { delay: 200 })] : []),
          ...(highlightLine ? [highlightActiveLine(), highlightActiveLineGutter()] : []),
          ...(showFoldGutter ? [foldGutter()] : []),
          EditorState.readOnly.of(readOnly || !editable),
          EditorView.editable.of(editable && !readOnly),
          EditorView.contentAttributes.of({
            'aria-label': label,
            'aria-multiline': 'true',
            'aria-readonly': String(readOnly || !editable),
          }),
          editorPlaceholder(placeholder || ''),
          sizeTheme,
        ]),
      })
    }, [config, editable, highlightLine, inputLanguage, label, placeholder, readOnly, showFoldGutter, sizeTheme])

    React.useEffect(() => {
      const view = viewRef.current
      if (!view) return
      view.dispatch(setDiagnostics(view.state, inputLanguage === 'json' ? diagnostics(view) : []))
    }, [inputLanguage])

    React.useEffect(() => {
      const view = viewRef.current
      if (!view || view.state.doc.toString() === value) return
      view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: value },
        selection: { anchor: Math.min(view.state.selection.main.head, value.length) },
        annotations: [externalValue.of(true), Transaction.addToHistory.of(false)],
      })
      view.dispatch(setDiagnostics(view.state, current.current.inputLanguage === 'json' ? diagnostics(view) : []))
    }, [value])

    return (
      <FormControl error={error} info={!editable || readOnly ? disabledText : info}>
        <div
          className="input-code"
          ref={containerRef}
          style={{
            borderBottom: `${error ? 2 : 1}px solid ${error ? '#ff5252' : 'rgb(32, 89, 120)'}`,
            marginTop: 2,
            width,
          }}
        />
        {(actions || personalizationList !== undefined) && (
          <Actions style={{ width }}>
            {actions}
            {personalizationList !== undefined && (
              <FormControlLabel
                control={<Switch checked={advanced} onChange={(_, checked) => setAdvanced(checked)} />}
                label="Advanced mode"
                sx={{ marginLeft: 'auto', marginRight: 0 }}
              />
            )}
          </Actions>
        )}
        {portals}
        <PzDetailsDialog
          details={selectedExpression && byReference.get(selectedExpression.reference)}
          expression={expressionDetails}
          trigger={trigger.current}
          getFallbackFocus={() => viewRef.current?.contentDOM}
          onClose={() => setSelectedExpression(undefined)}
        />
      </FormControl>
    )
  },
)

const Actions = styled('div')({
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  justifyContent: 'space-between',
  marginTop: '4px',
})

export default InputCode
